import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createTransientClock } from './transientAlertModel';

interface Props {
  /** Changing `message` restarts the 4-second clock (a new error is a new message). */
  message: string;
  /** Called when the 4 s pass or the ✕ is pressed (e.g. `mutation.reset`). */
  onClose: () => void;
  className?: string;
  children?: ReactNode;
}

/** D-285 - a short inline error that closes by itself after ~4 s and has a ✕ (aria-label from `aria.close`). */
export default function TransientAlert({ message, onClose, className = '', children }: Props) {
  const { t } = useTranslation();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const clock = useRef<ReturnType<typeof createTransientClock> | null>(null);

  useEffect(() => {
    const c = createTransientClock(() => closeRef.current());
    clock.current = c;
    c.start();
    return () => c.stop();
  }, [message]);

  return (
    <div
      className={`flex items-start gap-2 ${className}`}
      role="alert"
      onMouseEnter={() => clock.current?.stop()}
      onMouseLeave={() => clock.current?.start()}
      onFocus={() => clock.current?.stop()}
      onBlur={() => clock.current?.start()}
    >
      <div className="flex-1 min-w-0 flex items-start gap-2">{children ?? message}</div>
      <button
        type="button"
        onClick={onClose}
        className="p-0.5 rounded-md hover:bg-black/10 transition-colors flex-shrink-0"
        aria-label={t('aria.close')}
      >
        <X className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
      </button>
    </div>
  );
}
